import { StateCreator } from 'zustand'
import {
  logger,
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
  singletonQueryClient,
  getCompanyId,
} from '$utils'
import { SharedSlice } from '../../../../../slice'
import {
  IPayClassificationTimelineRecordRequest,
  PayClassification,
} from '$models'
import { payClassificationService } from '$services'

const invalidatePayClassificationDetail = (
  payClassificationId: string
): Promise<void> =>
  singletonQueryClient.invalidateQueries({
    queryKey: [
      payClassificationService.PAY_CLASSIFICATION_QUERY_KEYS.detail,
      getCompanyId(),
      payClassificationId,
    ],
  })

interface EditPayClassificationState {
  payClassification: PayClassification | null
}

export interface EditPayClassificationSlice extends EditPayClassificationState {
  /**
   * Reset PayClassification Slice
   */
  resetEditPayClassificationSlice: () => void

  /**
   * Get Pay Classification By id
   * @param payClassificationId PayClassification id
   */
  getPayClassification: (
    payClassificationId: string
  ) => Promise<PayClassification | null>

  /**
   * Update PayClassification
   * @param payClassification PayClassification to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns
   */
  updatePayClassification: (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Append a new timeline record (schedule a rate change) and refetch the parent classification.
   */
  createTimelineRecord: (
    payClassificationId: string,
    request: IPayClassificationTimelineRecordRequest
  ) => Promise<PayClassification | null>

  /**
   * Update an existing timeline record and refetch the parent classification.
   */
  updateTimelineRecord: (
    payClassificationId: string,
    recordId: string,
    request: IPayClassificationTimelineRecordRequest
  ) => Promise<PayClassification | null>

  /**
   * Soft-delete a timeline record and refetch the parent classification.
   */
  deleteTimelineRecord: (
    payClassificationId: string,
    recordId: string
  ) => Promise<PayClassification | null>
}

const initialState: EditPayClassificationState = {
  payClassification: null,
}

const createEditPayClassificationSlice: StateCreator<
  EditPayClassificationSlice & SharedSlice,
  [],
  [],
  EditPayClassificationSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditPayClassificationSlice: () => set(initialState),

  // State API calls
  getPayClassification: async (
    payClassificationId: string
  ): Promise<PayClassification | null> => {
    try {
      const response = await payClassificationService.getPayClassification(
        payClassificationId
      )

      set(() => ({
        payClassification: response,
      }))

      return response
    } catch (error) {
      logger.error(
        `Failed to retrieve pay classification ${payClassificationId}`,
        error
      )
      set(() => ({
        payClassification: null,
      }))

      exceptionMessageHandler(error, 'Failed to retrieve pay classification')
      return null
    }
  },

  updatePayClassification: async (
    payClassification: PayClassification,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPayClassification =
        await payClassificationService.updatePayClassification(
          payClassification
        )

      set(() => ({
        payClassification: updatedPayClassification,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      if (!updatedPayClassification) {
        logger.error(`${errorMessage} - updatedPayClassification doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  createTimelineRecord: async (
    payClassificationId: string,
    request: IPayClassificationTimelineRecordRequest
  ): Promise<PayClassification | null> => {
    try {
      await payClassificationService.createTimelineRecord(
        payClassificationId,
        request
      )
      await invalidatePayClassificationDetail(payClassificationId)
      const refreshed = await get().getPayClassification(payClassificationId)
      // getPayClassification shows its own error snackbar and returns null on
      // failure; only celebrate success once the refresh actually succeeded.
      if (refreshed) {
        enqueueLoadedSnackbar('Rate change scheduled', 'success')
      }
      return refreshed
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to schedule rate change')
      return null
    }
  },

  updateTimelineRecord: async (
    payClassificationId: string,
    recordId: string,
    request: IPayClassificationTimelineRecordRequest
  ): Promise<PayClassification | null> => {
    try {
      await payClassificationService.updateTimelineRecord(
        payClassificationId,
        recordId,
        request
      )
      await invalidatePayClassificationDetail(payClassificationId)
      const refreshed = await get().getPayClassification(payClassificationId)
      if (refreshed) {
        enqueueLoadedSnackbar('Rate record updated', 'success')
      }
      return refreshed
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update rate record')
      return null
    }
  },

  deleteTimelineRecord: async (
    payClassificationId: string,
    recordId: string
  ): Promise<PayClassification | null> => {
    try {
      await payClassificationService.deleteTimelineRecord(
        payClassificationId,
        recordId
      )
      await invalidatePayClassificationDetail(payClassificationId)
      const refreshed = await get().getPayClassification(payClassificationId)
      if (refreshed) {
        enqueueLoadedSnackbar('Rate record deleted', 'success')
      }
      return refreshed
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to delete rate record')
      return null
    }
  },
})

export default createEditPayClassificationSlice
